import {
    Box,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { LOADING_DATA_TEXT } from '../../constants';
import TeamMembersContainer from '../../containers/team-members';
import { discoverySessionAtom } from '../../stores/jotai/discovery-session';
import { loadStats, statsAtom } from '../../stores/jotai/stats';
import { useGetPopoverChartData } from '../../tanstackQuery/queries/analytics/useGetPopoverChartData';
import OverviewStats from '../analytics/OverviewStats';
import TeamAnalytics from '../analytics/TeamAnalytics';

interface OverviewInfoPopoverProps {
    team: any;
    cohort: any;
    base: any;
    value: number;
    handleChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const TabPanel=(props: any)=> {
    const { children, value, index, ...other } = props;
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`team-details-tabpanel-${index}`}
            aria-labelledby={`team-details-tab-${index}`}
            sx={{
                padding: "10px",
            }}
            {...other}
        >
            {value === index && children}
        </Box>
    );
}

const OverviewInfoPopover=({
    team,
    cohort,
    base,
    value,
    handleChange,
}: OverviewInfoPopoverProps)=> {
    const [chartData, setChartData] = useState(undefined);
    const [discoverySession] = useAtom(discoverySessionAtom);
    const [stats,setStats] = useAtom(statsAtom);
    const theme = useTheme();
    const [chartDataUrl, setChartDataUrl] = useState<string | null>(null)  
    
   const { data: popoverChartData } = useGetPopoverChartData(chartDataUrl, { enabled: chartDataUrl ? true : false });

    useEffect(() => {
        loadStats(setStats, discoverySession,team?.groupID);
    }, [team, discoverySession]);

    useEffect(() => {
        let data_artifact = "";

        if (chartData === undefined) {
            if (cohort) {
                data_artifact = cohort?.chartDataUrl;
            } else {
                data_artifact = team!.chartDataUrl;
            }

            if (data_artifact !== "") {
                setChartDataUrl(data_artifact)
            }
        }
    }, [cohort, team]);


    useEffect(() => {
        if(popoverChartData !== undefined) {
            setChartData(popoverChartData);
        }
    }, [team, cohort, chartDataUrl, popoverChartData])

    const loader = (
        <Stack
            sx={{ padding: "10px 0", width: "500px" }}
            spacing={2}
        >
            <Typography>{LOADING_DATA_TEXT}</Typography>
            <LinearProgress />
        </Stack>
    );

    const renderMetricsTab = () => {
        let tabContent = null;

        if (!chartData) {
            tabContent = loader;
        } else {
            const teamName = team?.name;
            const { cohort_stats } = chartData;
            const teamStats = cohort_stats[teamName];

            tabContent = (
                <>
                    <TeamAnalytics chartData={chartData} statAtomState={stats}/>
                    <Box>
                        <OverviewStats
                            interviews={teamStats?.total_interviews}
                            insights={teamStats?.total_insights}
                            hypotheses={teamStats?.total_hypo}
                            validHypotheses={teamStats?.total_hypo_val}
                            invalidHypotheses={teamStats?.total_hypo_inval}
                        />
                    </Box>
                </>
            );
        }

        return (
            <Stack direction="row" spacing={1}>
                {tabContent}
            </Stack>
        );
    };

    return (
        <>
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    background: "white",
                }}
            >
                <Tabs
                    sx={{ marginBottom: "-1.5px" }}
                    value={value}
                    onChange={handleChange}
                    aria-label="analytics type for your team"
                >
                    <Tab label="Metrics" />
                    <Tab label="Team Members" />
                </Tabs>
            </Box>
            <Box
                sx={{
                    maxHeight: "80vh",
                    overflow: "auto",
                    ".TeamAnalytics-container": {
                        width: "700px",
                    },
                }}
            >
                <TabPanel
                    value={value}
                    index={0}
                >
                    {renderMetricsTab()}
                </TabPanel>
                <TabPanel
                    value={value}
                    index={1}
                >
                    <Box
                        sx={{
                            background: "white",
                            margin: "0px",
                            width: "700px",
                        }}
                    >
                        <TeamMembersContainer
                            members={base?.members}
                            theme={theme}
                        />
                    </Box>
                </TabPanel>
            </Box>
        </>
    );
}

export default OverviewInfoPopover;